import type { TeamConnectionStatus } from '../../hooks/useConnectExistingTeam';

/**
 * Message shown in the tab configuration page for the current incident/Team
 * pairing, or null when there is nothing worth saying. Returns null outside a
 * Team (personal or static tab scope), where there is nothing to connect.
 */
export function getTeamConnectionMessage(
  teamGroupId: string | undefined,
  status: TeamConnectionStatus | undefined,
  callerNotOnRoster = false
): string | null {
  if (!teamGroupId) return null;

  if (callerNotOnRoster) {
    return 'You are not a member of this incident workspace, so it cannot be connected to this Team site. Select a workspace you belong to, or ask to be added to its roster.';
  }
  if (!status) return null;

  if (status.connectedToAnotherTeam) {
    return 'This incident workspace is already connected to a different Team site. Select a different incident workspace to continue.';
  }
  if (status.connectedToThisTeam) {
    return 'This incident workspace is already connected to this Team site. Saving will add the tab without changing the connection.';
  }
  if (status.teamLinkedElsewhere) {
    return 'This Team site is already connected to a different incident workspace, and a Team site can only be connected to one. Add PRATUS to a Team site that is not yet connected, or disconnect the other workspace first.';
  }
  return null;
}

/**
 * Blocks the save when the caller is not on the incident roster, when the
 * incident is bound to a different Team, or when the Team is already bound to
 * another workspace -- the incident-to-Team mapping is 1:1 in both directions.
 *
 * All three are resolved before the user clicks Save, because Teams keeps the
 * last `notifyFailure` reason on screen indefinitely and it goes stale as soon
 * as a different incident is selected.
 */
export function connectionBlocksSave(
  teamGroupId: string | undefined,
  status: TeamConnectionStatus | undefined,
  callerNotOnRoster = false
): boolean {
  return Boolean(
    teamGroupId &&
      (callerNotOnRoster ||
        status?.connectedToAnotherTeam ||
        status?.teamLinkedElsewhere)
  );
}

/**
 * Should a failed connect attempt still create the tab?
 *
 * A conflict or an authorization failure is a definitive answer from the
 * server, so the dialog stays open and the user is told why. Anything else --
 * COMS unreachable, a 5xx, a network blip -- is inconclusive, and adding a tab
 * has never required a Teams linkage, so it must not regress into a hard
 * failure.
 */
export function connectFailureBlocksSave(
  httpStatus: number | undefined
): boolean {
  return linkageFailureIsDefinitive(httpStatus);
}

/**
 * Did the server give a definitive answer about a linkage change, as opposed to
 * simply failing to answer?
 *
 * A conflict or an authorization failure is a decision COMS made and stands by,
 * so the user must be told. Anything else -- COMS unreachable, a 5xx, a network
 * blip -- leaves the linkage state unknown, and neither adding nor removing a
 * tab should be blocked on an unknown.
 */
export function linkageFailureIsDefinitive(
  httpStatus: number | undefined
): boolean {
  return httpStatus === 409 || httpStatus === 403;
}
